'use client'

import { NftData } from '@models/Nft'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import axios from 'axios'
import { useAsync } from 'react-async-hook'
import { DefaultFilterOptions } from '@utils/explorer'
import { ExplorerAttribute } from 'types/explorer'

type FilterOptions = {
  attributes: ExplorerAttribute
  number: string
}

const DefaultFilters: FilterOptions = {
  attributes: DefaultFilterOptions,
  number: '',
}

const useCollectionHook = (
  pageSize = 500,
  initialFilters: FilterOptions = DefaultFilters,
) => {
  const [nfts, setNfts] = useState<null | NftData[]>(null)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [searchEnd, setSearchEnd] = useState<boolean>(false)
  const [currentAttributes, setCurrentAttributes] =
    useState<{ name: keyof ExplorerAttribute; items: string[] }[]>()
  const [avatars, setAvatars] = useState<Map<string, string>>(null)

  const loadAvatars = useCallback(async () => {
    const response = await axios.get('/api/getAttributes', {
      params: {
        select: JSON.stringify(['name', 'avatar']),
      },
    })

    if (response.status >= 400) {
      throw new Error(response.data.message)
    }

    const attributes = response.data.attributes

    const avatars = new Map<string, string>()

    attributes.forEach((attribute: { name: string; avatar: string }) => {
      avatars.set(attribute.name, attribute.avatar)
    })

    setAvatars(avatars)
  }, [])

  const loadData = async () => {
    setError(false)
    setSearchEnd(false)
    setLoading(true)
    try {
      if (!avatars) await loadAvatars()

      const { data } = await axios.get('/api/fetchFilteredNftCollection', {
        params: {
          pageSize,
          pageNumber,
        },
      })

      setNfts((prevNfts) => (pageNumber === 1 ? data : [...prevNfts, ...data]))
      setSearchEnd(true)
    } catch (_) {
      setError(true)
      console.error('Error:', _)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (nfts) {
      const attributes = filterAttributes(nfts)
      setCurrentAttributes(attributes)
    }
  }, [nfts])

  const filterAttributes = (data: NftData[]) => {
    return data.reduce(
      (acc, nft) => {
        const keys = Object.keys(nft.attribute) as Array<
          keyof typeof nft.attribute
        >

        keys.forEach((key) => {
          if (key === 'listed' || key === 'staked' || key === 'aura') return

          const attributeValue = nft.attribute[key] as string
          if (attributeValue.toLocaleLowerCase() === 'none') return

          let attr = acc.find((a) => a.name === key)

          if (attr) {
            // Ensure no duplicate items
            if (!attr.items.includes(attributeValue)) {
              attr.items.push(attributeValue)
            }
          } else {
            acc.push({
              name: key,
              items: [attributeValue],
            })
          }
        })

        return acc
      },
      [] as { name: keyof ExplorerAttribute; items: string[] }[],
    )
  }

  useAsync(loadData, [pageNumber])

  const fetchMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1)
  }

  return {
    nfts,
    avatars,
    isLoading: loading || !nfts,
    error,
    searchEnd,
    currentAttributes,
    fetchMore,
    loadData,
  }
}
export type CollectionContextManager = ReturnType<typeof useCollectionHook>

const CollectionContext = createContext<CollectionContextManager | null>(null)

const { Provider } = CollectionContext

const CollectionProvider = ({ children }: { children: ReactNode }) => {
  return <Provider value={useCollectionHook()}>{children}</Provider>
}

const useCollection = (): CollectionContextManager => {
  const context = useContext(CollectionContext)
  if (!context) {
    throw new Error('useCollection has to be used within <CollectionProvider>')
  }
  return context
}
export { useCollection, CollectionProvider }

export default useCollection
